/**
 * AddressFinder Component
 * Main component that provides address lookup functionality using Ideal Postcodes API
 */

import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import PostcodeSearch from './components/PostcodeSearch';
import AddressList from './components/AddressList';
import ManualAddressForm from './components/ManualAddressForm';
import { useAddressLookup } from './hooks/useAddressLookup';
import { UI_STATES } from './utils/addressConstants';

/**
 * AddressFinder Component
 * @param {Object} props - Component props
 * @param {Object} props.initialAddress - Initial address values
 * @param {Function} props.onAddressSelect - Callback when address is selected
 * @param {Function} props.onError - Callback when error occurs
 * @param {boolean} props.showManualEntry - Whether to show manual entry option
 * @param {boolean} props.autoSelectSingle - Auto-select if only one result
 * @param {boolean} props.showQueryInput - Whether to show building query input
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.placeholder - Placeholder text for postcode input
 * @param {number} props.maxResults - Maximum number of results to display
 * @param {boolean} props.hideActionButtons - Whether to hide Change/Edit buttons after selection
 */
const AddressFinder = ({
  initialAddress = {},
  onAddressSelect,
  onError,
  showManualEntry = true,
  autoSelectSingle = true,
  showQueryInput = false,
  className = '',
  placeholder,
  maxResults = 500,
  onStateChange,
  hideActionButtons = false
}) => {
  const {
    uiState,
    postcode,
    query,
    addresses,
    selectedAddress,
    previousSelectedAddress,
    error,
    isLoading,
    validationMessage,
    handlePostcodeChange,
    handleQueryChange,
    handleAddressSelect,
    searchAddresses,
    switchToManualEntry,
    switchToAddressSearch,
    cancelAndRestoreAddress,
    reset,
    clearError
  } = useAddressLookup({
    onAddressSelect,
    onError,
    autoSelectSingle,
    maxResults,
    initialAddress
  });

  /**
   * Notify parent of state changes
   */
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        uiState,
        switchToAddressSearch: showManualEntry ? switchToAddressSearch : null
      });
    }
  }, [uiState, onStateChange, switchToAddressSearch, showManualEntry]);

  /**
   * Handle manual address form changes
   */
  const handleManualAddressChange = (addressData) => {
    if (onAddressSelect) {
      onAddressSelect(addressData);
    }
  };

  /**
   * Handle search trigger from search form
   */
  const handleSearchTrigger = (postcodeValue, queryValue) => {
    searchAddresses(postcodeValue, queryValue);
  };

  /**
   * Render error state
   */
  const renderError = () => {
    if (!error) return null;

    return (
      <div className="mt-4 p-4 border border-red-200 rounded-md bg-red-50">
        <div className="flex items-start gap-2 mb-3">
          <span className="text-lg flex-shrink-0" aria-hidden="true">⚠️</span>
          <div className="text-sm text-red-800 leading-relaxed">
            {error}
          </div>
        </div>
        <div className="flex gap-3 sm:flex-row flex-col">
          <button
            type="button"
            onClick={clearError}
            className="px-3 py-1.5 border border-red-600 rounded bg-red-600 text-white text-xs font-medium cursor-pointer transition-colors hover:bg-red-700"
          >
            Try Again
          </button>
          {showManualEntry && (
            <button
              type="button"
              onClick={switchToManualEntry}
              className="px-3 py-1.5 border border-red-600 rounded bg-white text-red-600 text-xs font-medium cursor-pointer transition-colors hover:bg-red-50"
            >
              Enter Manually
            </button>
          )}
        </div>
      </div>
    );
  };

  /**
   * Render success state with selected address
   */
  const renderSelectedAddress = () => {
    if (uiState !== UI_STATES.SELECTED || !selectedAddress) return null;

    const addressDisplay = [
      selectedAddress.line1,
      selectedAddress.line2,
      selectedAddress.line3,
      selectedAddress.city,
      selectedAddress.county,
      selectedAddress.postcode
    ].filter(Boolean).join(', ');

    return (
      <div className="mt-4 p-4 border border-green-200 rounded-md bg-green-50">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg" aria-hidden="true">✅</span>
          <h4 className="m-0 text-sm font-semibold text-green-800">Address Selected</h4>
        </div>
        <div className="text-sm text-gray-700 mb-3 leading-relaxed">
          {addressDisplay}
        </div>
        {!hideActionButtons && (
          <div className="flex gap-3 sm:flex-row flex-col">
            <button
              type="button"
              onClick={() => switchToAddressSearch()}
              className="px-3 py-1.5 border border-gray-300 rounded bg-white text-xs font-medium cursor-pointer transition-colors hover:bg-gray-50 hover:border-gray-400"
            >
              Change Address
            </button>
            {showManualEntry && (
              <button
                type="button"
                onClick={switchToManualEntry}
                className="px-3 py-1.5 border border-gray-300 rounded bg-white text-xs font-medium cursor-pointer transition-colors hover:bg-gray-50 hover:border-gray-400"
              >
                Edit Manually
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`w-full max-w-2xl ${className}`}>
      {/* Address Lookup Mode */}
      {[UI_STATES.IDLE, UI_STATES.SEARCHING, UI_STATES.RESULTS].includes(uiState) && (
        <div className="flex flex-col gap-4">
          {/* Show cancel button if there's a previous selected address */}
          {previousSelectedAddress && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={cancelAndRestoreAddress}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel & Return to Selected Address
              </button>
            </div>
          )}

          <PostcodeSearch
            postcode={postcode}
            query={query}
            onPostcodeChange={handlePostcodeChange}
            onQueryChange={handleQueryChange}
            onSearch={handleSearchTrigger}
            isLoading={isLoading}
            validationMessage={validationMessage}
            uiState={uiState}
            showQueryInput={showQueryInput}
          />
          
          {uiState === UI_STATES.RESULTS && addresses.length > 0 && (
            <>
              {/* Overlay background */}
              <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-40 overflow-hidden" onClick={reset}>
                <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden m-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-teal to-teal-600">
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-white">Address Results</h2>
                      <p className="text-sm text-white/90 mt-1">
                        {addresses.length} address{addresses.length !== 1 ? 'es' : ''} found - Click to select
                      </p>
                    </div>
                    <button
                      onClick={reset}
                      className="text-white hover:text-gray-200 transition-colors duration-200"
                      aria-label="Close address results"
                    >
                      <X size={24} />
                    </button>
                  </div>
                  
                  <div className="p-4 md:p-6 overflow-y-auto flex-1 overscroll-contain">
                    <AddressList
                      addresses={addresses}
                      onAddressSelect={handleAddressSelect}
                      selectedAddress={selectedAddress}
                      isLoading={false}
                      maxHeight={9999}
                      className="border-none shadow-none"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          
          {showManualEntry && uiState !== UI_STATES.SEARCHING && (
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={switchToManualEntry}
                className="text-blue-600 bg-transparent border-none text-sm underline cursor-pointer transition-colors hover:text-blue-700"
              >
                Can't find your address? Enter it manually
              </button>
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Mode */}
      {uiState === UI_STATES.MANUAL && (
        <div className="mt-4">
          {/* Show cancel button if there's a previous selected address */}
          {previousSelectedAddress && (
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={cancelAndRestoreAddress}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel & Return to Selected Address
              </button>
            </div>
          )}

          <ManualAddressForm
            initialValues={initialAddress}
            onAddressChange={handleManualAddressChange}
            onSwitchToLookup={switchToAddressSearch}
            showSwitchOption={true}
          />
        </div>
      )}

      {/* Selected Address Display */}
      {renderSelectedAddress()}

      {/* Error Display */}
      {renderError()}

    </div>
  );
};

export default AddressFinder;